<script setup>
// Files to download: documents and images from the organization's
// uploads library, each a link a visitor can open or save.
//
// Each item carries the absolute link the library handed out, because
// a custom domain serves this page from the customer's own hostname and
// a root-relative /files/... there would be rewritten into the site and
// miss. safeFileUrl decides which links are drawn at all.
import { computed, inject } from 'vue';
import { safeFileUrl } from '../filesBlock.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const inEditor = inject('tiqEditorCanvas', false);

const items = computed(() => (props.config.items || [])
    .map((it) => ({ ...it, href: safeFileUrl(it) }))
    .filter((it) => it.href || inEditor));

function formatSize(bytes) {
    const n = Number(bytes) || 0;
    if (!n) return '';
    if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
const ext = (it) => String(it.extension || '').replace(/[^a-z0-9]/gi, '').slice(0, 5).toUpperCase();

const cssVars = computed(() => {
    const s = props.config.style || {};
    const v = {};
    if (s.bg) v['--block-bg'] = s.bg;
    if (s.text) v['--block-text'] = s.text;
    if (s.heading) v['--text-heading'] = s.heading;
    if (s.item) v['--text-item'] = s.item;
    v['--files-accent'] = s.accent || props.accent || 'var(--accent, #dd9b36)';
    return v;
});
</script>

<template>
    <section v-if="items.length || inEditor" class="tiq-files" :id="config.anchor_id || null" :style="cssVars" data-files-block>
        <div class="tiq-files-inner">
            <h2 v-if="config.heading" class="tiq-files-h">{{ config.heading }}</h2>
            <p v-if="config.intro" class="tiq-files-intro">{{ config.intro }}</p>
            <ul v-if="items.length" class="tiq-files-list">
                <li v-for="(it, i) in items" :key="it.uuid || i">
                    <component :is="it.href ? 'a' : 'div'" class="tiq-files-item"
                               :href="it.href || null" :target="it.href ? '_blank' : null"
                               :rel="it.href ? 'noopener' : null">
                        <span class="tiq-files-ext">{{ ext(it) || 'FILE' }}</span>
                        <span class="tiq-files-main">
                            <span class="tiq-files-title">{{ it.title || 'File' }}</span>
                            <span v-if="it.description" class="tiq-files-desc">{{ it.description }}</span>
                            <span class="tiq-files-meta">{{ [ext(it), formatSize(it.size_bytes)].filter(Boolean).join(' · ') }}</span>
                        </span>
                        <svg class="tiq-files-dl" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12 4v11m0 0l-4.5-4.5M12 15l4.5-4.5M5 19h14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </component>
                </li>
            </ul>
            <p v-else class="tiq-files-empty">Choose files in the editor to list them here for download.</p>
        </div>
    </section>
</template>

<style scoped>
.tiq-files {
    background: var(--block-bg, transparent);
    color: var(--block-text, var(--th-ink, #1a1a1a));
    padding: clamp(36px, 5vw, 64px) var(--th-gutter, 24px);
}
.tiq-files-inner { max-width: 880px; margin: 0 auto; }
.tiq-files-h {
    font-family: var(--th-display, 'Barlow Condensed', sans-serif);
    font-weight: 700;
    font-size: clamp(26px, 3.4vw, 38px);
    line-height: 1.1;
    margin: 0;
    color: var(--text-heading, inherit);
}
.tiq-files-intro { margin: 10px 0 0; line-height: 1.55; color: var(--th-muted, #4b5563); max-width: 640px; }
.tiq-files-list { list-style: none; margin: 22px 0 0; padding: 0; display: grid; gap: 10px; }
.tiq-files-item {
    display: flex; align-items: center; gap: 14px;
    padding: 14px 16px;
    background: #fff;
    border: 1px solid var(--th-line, #e5e7eb);
    border-radius: var(--r, 8px);
    color: var(--text-item, inherit);
    text-decoration: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
a.tiq-files-item:hover { border-color: var(--files-accent); box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06); }
.tiq-files-ext {
    flex: none; width: 46px; height: 46px; border-radius: 6px;
    display: grid; place-items: center;
    font: 700 11px/1 var(--th-mono, ui-monospace, monospace); letter-spacing: 0.04em;
    background: color-mix(in srgb, var(--files-accent) 14%, #fff);
    color: color-mix(in srgb, var(--files-accent) 70%, #000);
}
.tiq-files-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.tiq-files-title { font-weight: 600; font-size: 16px; overflow-wrap: anywhere; }
.tiq-files-desc { font-size: 14px; color: var(--th-muted, #4b5563); }
.tiq-files-meta { font-size: 12px; color: var(--th-faint, #6b7280); }
.tiq-files-dl { flex: none; width: 22px; height: 22px; color: var(--files-accent); }
.tiq-files-empty { margin: 18px 0 0; color: #9ca3af; font-style: italic; }
</style>
